import React, { useEffect } from 'react';
import authService from '@services/authService';

/**
 * TrackingPixels — one-shot boot-time injector for the marketing /
 * analytics pixels the admin configured in Integration Settings →
 * Tracking Pixels. Runs exactly once per page load: fetches
 * /public-config, then appends a `<script>` for each ID that's set.
 *
 * Non-goals: this component does NOT report page views to the pixels
 * — the vendor snippets (gtag / fbq / etc.) do that themselves once
 * initialised. It also does NOT re-fire on route changes; SPA route
 * tracking should live inside a router listener that calls
 * `gtag('event', 'page_view')` / `fbq('track', 'PageView')` etc.
 *
 * Renders no DOM.
 */
const INJECTED_ATTR = 'data-vw-pixel';

function inject(id, html) {
  // Idempotent — hot reload / Strict-Mode double-mounts should not
  // duplicate the pixel snippets, which would double every event.
  if (document.head.querySelector(`script[${INJECTED_ATTR}="${id}"]`)) return;
  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;
  Array.from(wrapper.childNodes).forEach(node => {
    if (node.nodeType === 1 && node.tagName === 'SCRIPT') {
      const s = document.createElement('script');
      Array.from(node.attributes).forEach(a => s.setAttribute(a.name, a.value));
      s.setAttribute(INJECTED_ATTR, id);
      if (node.src) s.src = node.src;
      s.text = node.textContent || '';
      document.head.appendChild(s);
    } else if (node.nodeType === 1 && node.tagName === 'NOSCRIPT') {
      // noscript children go into <body> so they render for bots that
      // ignore JS but still respect noscript (some analytics products
      // still ship a noscript img beacon).
      const n = document.createElement('noscript');
      n.setAttribute(INJECTED_ATTR, id + ':noscript');
      n.innerHTML = node.innerHTML;
      document.body.appendChild(n);
    }
  });
}

function ga4Snippet(id) {
  return `
    <script async src="https://www.googletagmanager.com/gtag/js?id=${id}"></script>
    <script>
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${id}');
    </script>`;
}

function gtmSnippet(id) {
  return `
    <script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');</script>
    <noscript><iframe src="https://www.googletagmanager.com/ns.html?id=${id}" height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>`;
}

function metaPixelSnippet(id) {
  return `
    <script>
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${id}');
      fbq('track', 'PageView');
    </script>
    <noscript><img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${id}&ev=PageView&noscript=1"/></noscript>`;
}

function linkedinInsightSnippet(id) {
  return `
    <script>
      _linkedin_partner_id = "${id}";
      window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
      window._linkedin_data_partner_ids.push(_linkedin_partner_id);
    </script>
    <script>
      (function(l) {
        if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
        window.lintrk.q=[]}
        var s = document.getElementsByTagName("script")[0];
        var b = document.createElement("script");
        b.type = "text/javascript";b.async = true;
        b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
        s.parentNode.insertBefore(b, s);
      })(window.lintrk);
    </script>
    <noscript><img height="1" width="1" style="display:none;" alt="" src="https://px.ads.linkedin.com/collect/?pid=${id}&fmt=gif" /></noscript>`;
}

function pinterestTagSnippet(id) {
  return `
    <script>
      !function(e){if(!window.pintrk){window.pintrk = function () {
      window.pintrk.queue.push(Array.prototype.slice.call(arguments))};var
        n=window.pintrk;n.queue=[],n.version="3.0";var
        t=document.createElement("script");t.async=!0,t.src=e;var
        r=document.getElementsByTagName("script")[0];
        r.parentNode.insertBefore(t,r)}}("https://s.pinimg.com/ct/core.js");
      pintrk('load', '${id}');
      pintrk('page');
    </script>
    <noscript><img height="1" width="1" style="display:none;" alt="" src="https://ct.pinterest.com/v3/?tid=${id}&noscript=1" /></noscript>`;
}

function tiktokPixelSnippet(id) {
  return `
    <script>
      !function (w, d, t) {
        w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
        ttq.load('${id}');
        ttq.page();
      }(window, document, 'ttq');
    </script>`;
}

const TrackingPixels = () => {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cfg = await authService.getPublicConfig();
      if (cancelled) return;
      const p = cfg?.pixels || {};
      if (p.ga4)       inject('ga4:' + p.ga4, ga4Snippet(p.ga4));
      if (p.gtm)       inject('gtm:' + p.gtm, gtmSnippet(p.gtm));
      if (p.facebook)  inject('fbq:' + p.facebook, metaPixelSnippet(p.facebook));
      if (p.linkedin)  inject('li:' + p.linkedin, linkedinInsightSnippet(p.linkedin));
      if (p.pinterest) inject('pin:' + p.pinterest, pinterestTagSnippet(p.pinterest));
      if (p.tiktok)    inject('ttq:' + p.tiktok, tiktokPixelSnippet(p.tiktok));
    })();
    return () => { cancelled = true; };
  }, []);

  return null;
};

export default TrackingPixels;
